import { type DropdownProps } from "semantic-ui-react";

import Dropdown from "features/Library/Common/Fields/Dropdown";

import { ValidationThrottler } from "features/Library/Common/Fields/ValidationThrottler";
import { WithFormikPayload } from "features/Library/Common/Fields/WithFormikPayload";
import { type IQuestion, type QuestionTypeOptions } from "features/Library/Common/models";
import { type QuestionHeaderPropsBase } from "features/Library/Common/props";
import { AssessmentQuestionType, type MultipleChoiceQuestion } from "features/Library/Assessments/types/state";
import { MapQuestion } from "../AssessmentProviders/QuestionMapper";
import { QuestionHeader } from "features/Library/Common/QuestionsForm/Questions/QuestionHeader/QuestionHeader";
import { MIN_QUESTIONS_LENGTH } from "features/Library/Common/constants";
import { Checkbox } from "features/Library/Common/QuestionsForm/Questions/QuestionFields/CheckBox/Checkbox";
import React from "react";

export type AssessmentQuestionHeaderProps = QuestionHeaderPropsBase<IQuestion<any>>;

export const AssessmentQuestionHeader = (props: AssessmentQuestionHeaderProps) => {
  const {
    question,
    validatedFieldProps,
    index,
    onChanged,
    disabled,
    onQuestionTypeChanged,
    propertyNamePrefix,
    onRemove,
  } = props;

  const questionTypeOptions: Array<QuestionTypeOptions> = [
    { text: "Multiple Choice Text (multi-select)", value: AssessmentQuestionType.MultipleChoiceTextQuestion },
    { text: "Multiple Choice Text (single-select)", value: AssessmentQuestionType.SingleChoiceTextQuestion },
  ];

  const handleTypeDropdownChange = (_: any, data: DropdownProps) => {
    const value = data.value as AssessmentQuestionType;
    const mapResult = MapQuestion(value, question);

    onQuestionTypeChanged(mapResult.question);
    onChanged();
  };

  const handleRemove = () => onRemove(index);

  return (
    <>
      <QuestionHeader
        title={`Question ${index + 1}`}
        canRemove={!disabled && index >= MIN_QUESTIONS_LENGTH}
        onRemove={handleRemove}
      >
        {question.questionType === AssessmentQuestionType.MultipleChoiceTextQuestion && (
          <Checkbox
            validatedFieldProps={validatedFieldProps}
            label={"Require all correct answers to be selected"}
            onChanged={onChanged}
            propertyName={`${propertyNamePrefix}question.requireAllCorrectAnswers`}
            isChecked={(question as IQuestion<MultipleChoiceQuestion>).question.requireAllCorrectAnswers}
            tooltip={"Be sure to select ALL correct answers."}
            disabled={disabled}
          />
        )}
      </QuestionHeader>

      <WithFormikPayload
        propertyName={`${propertyNamePrefix}questionType`}
        pathToValidate={`questions.${index}`}
        {...validatedFieldProps}
        selfValidation
        render={(formikProps) => (
          <ValidationThrottler {...formikProps} value={question.questionType}>
            <Dropdown
              label="Question Type"
              value={question.questionType}
              options={questionTypeOptions}
              errorPosition={"bottom"}
              disabled={disabled}
              setFieldTouched={validatedFieldProps.setFieldTouched}
              setFieldValue={validatedFieldProps.setFieldValue}
              onChange={handleTypeDropdownChange}
              {...formikProps}
              disableValidation
            />
          </ValidationThrottler>
        )}
      />
    </>
  );
};
